import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQueryState } from 'nuqs';
import { Radio, Bot, Plug, Users, Building2 } from 'lucide-react';
import { CardGrid } from '@reflex/ui/components/shared/CardGrid';
import { StatCard } from '@reflex/ui/components/shared/StatCard';
import { SectionCard } from '@reflex/ui/components/shared/SectionCard';
import { EmptyState } from '@reflex/ui/components/shared/EmptyState';
import { timeAgo } from '@reflex/ui/lib/format';
import { DataTable, type DataTableColumn } from '@/components/shared/DataTable';
import { FilterSearchSelect } from '@/components/filters/FilterSearchSelect';
import type { FilterOption } from '@/components/filters/filter-types';
import { AgentStatusChip, AgentStatusDot } from '@/components/status/agent/AgentStatusChip';
import { StreamFlowCell } from '@/components/monitor/StreamFlowCell';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { useUsers } from '@/hooks/auth/useUsers';
import { useOrgRoutes } from '@/lib/useOrgRoutes';
import { ROUTES } from '@/lib/routes';
import { getUserDisplayName } from '@/lib/agent-utils';
import type { UsageOrgTotals, UsageSnapshot, UsageStream, UsageUserTotals } from '@reflex/shared';
import type { User } from '@/lib/types';

const ALL_ORGS = '__all__';
/** Sentinel for the "no organization" bucket (agentless flow streams, anon sockets). */
const NO_ORG = '__none__';

/** Label for an org row/option, falling back through name, slug, then a literal. */
function orgLabel(organizationId: string, name: string | null, slug: string | null): string {
  if (organizationId === '') return 'No organization';
  return name ?? slug ?? organizationId;
}

interface AdminUsagePanelProps {
  snapshot: UsageSnapshot | null;
}

/**
 * Super-admin usage breakdown. Surfaces system-wide active streams, running
 * agents, and connected sockets, broken down per organization, with an org
 * filter that scopes the stat tiles, the active-streams table, and the
 * per-user table to a single org (or all orgs).
 */
export function AdminUsagePanel({ snapshot }: AdminUsagePanelProps) {
  const { data: users } = useUsers();
  const orgRoutes = useOrgRoutes();

  const [rawOrg, setRawOrg] = useQueryState('usageOrg');
  // null param means "all orgs"; the NO_ORG sentinel filters to the
  // empty-string bucket; any other value is a concrete organization id.
  const orgFilter: string | null = rawOrg === null ? null : rawOrg === NO_ORG ? '' : rawOrg;

  const userMap = useMemo(() => {
    const map = new Map<string, User>();
    for (const u of users ?? []) map.set(u.id, u);
    return map;
  }, [users]);

  const orgOptions = useMemo<FilterOption[]>(() => {
    const opts: FilterOption[] = [{ value: ALL_ORGS, label: 'All orgs' }];
    for (const org of snapshot?.perOrg ?? []) {
      opts.push({
        value: org.organizationId === '' ? NO_ORG : org.organizationId,
        label: orgLabel(org.organizationId, org.name, org.slug),
      });
    }
    return opts;
  }, [snapshot]);

  // Stat tiles reflect the selected org (from perOrg) or the system totals.
  const tiles = useMemo(() => {
    if (!snapshot) return null;
    if (orgFilter === null) {
      return {
        activeStreams: snapshot.totals.activeStreams,
        runningAgents: snapshot.totals.runningAgents,
        connectedClients: snapshot.totals.connectedClients,
      };
    }
    const row = snapshot.perOrg.find((o) => o.organizationId === orgFilter);
    return {
      activeStreams: row?.activeStreams ?? 0,
      runningAgents: row?.runningAgents ?? 0,
      connectedClients: row?.connectedSockets ?? 0,
    };
  }, [snapshot, orgFilter]);

  const filteredStreams = useMemo(
    () =>
      (snapshot?.streams ?? []).filter((s) => orgFilter === null || s.organizationId === orgFilter),
    [snapshot, orgFilter],
  );

  const filteredPerUser = useMemo(
    () =>
      (snapshot?.perUser ?? []).filter((r) => orgFilter === null || r.organizationId === orgFilter),
    [snapshot, orgFilter],
  );

  const showUserOrgColumn = orgFilter === null;

  const orgColumns: DataTableColumn<UsageOrgTotals>[] = useMemo(
    () => [
      {
        header: 'Organization',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (row) =>
          row.organizationId === '' ? (
            <span className="flex items-center gap-2 text-sm">
              <Building2 className="icon-sm shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground">
                {orgLabel(row.organizationId, row.name, row.slug)}
              </span>
            </span>
          ) : (
            <Link
              to={ROUTES.admin.monitorOrg(row.organizationId)}
              className="flex items-center gap-2 text-sm hover:underline"
            >
              <Building2 className="icon-sm shrink-0 text-muted-foreground" />
              <span className="font-medium">
                {orgLabel(row.organizationId, row.name, row.slug)}
              </span>
            </Link>
          ),
      },
      {
        header: 'Running agents',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.runningAgents,
      },
      {
        header: 'Active streams',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.activeStreams,
      },
      {
        header: 'Agentless',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums text-muted-foreground',
        cell: (row) => row.agentlessStreams,
      },
      {
        header: 'Sockets',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.connectedSockets,
      },
    ],
    [],
  );

  const streamColumns: DataTableColumn<UsageStream>[] = useMemo(
    () => [
      {
        header: 'Stream',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (stream) => (
          <span className="font-mono text-xs text-muted-foreground">
            {stream.streamId.length > 12
              ? `${stream.streamId.slice(0, 8)}…${stream.streamId.slice(-4)}`
              : stream.streamId}
          </span>
        ),
      },
      {
        header: 'Agent',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (stream) =>
          stream.agentId ? (
            <Link
              to={orgRoutes.agent(stream.agentId)}
              className="flex items-center gap-2 hover:underline"
            >
              {stream.agentStatus && <AgentStatusDot status={stream.agentStatus} />}
              <span className="max-w-[220px] truncate font-medium">
                {stream.agentName ?? stream.agentId}
              </span>
            </Link>
          ) : stream.flowName ? (
            <StreamFlowCell flowName={stream.flowName} flowRunId={stream.flowRunId} />
          ) : (
            <span className="text-xs text-muted-foreground">automation / no agent</span>
          ),
      },
      {
        header: 'Status',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (stream) =>
          stream.agentStatus ? (
            <AgentStatusChip status={stream.agentStatus} agentId={stream.agentId ?? undefined} />
          ) : (
            <span className="text-xs text-muted-foreground">--</span>
          ),
      },
      {
        header: 'Owner',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (stream) => {
          const user = stream.ownerUserId ? userMap.get(stream.ownerUserId) : null;
          return user ? (
            <Link
              to={ROUTES.userProfile(user.id)}
              className="flex items-center gap-1.5 hover:underline"
            >
              <UserAvatar
                avatarUrl={user.avatarUrl ?? undefined}
                userName={getUserDisplayName(user)}
                size="xs"
              />
              <span className="text-xs text-muted-foreground">{getUserDisplayName(user)}</span>
            </Link>
          ) : (
            <span className="text-xs text-muted-foreground">--</span>
          );
        },
      },
      {
        header: 'Subs',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (stream) => stream.subscribers,
      },
      {
        header: 'Events',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (stream) => stream.eventCount,
      },
      {
        header: 'Last event',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right',
        cell: (stream) => (
          <span className="text-xs text-muted-foreground">
            {stream.lastEventAt ? timeAgo(stream.lastEventAt) : '--'}
          </span>
        ),
      },
    ],
    [userMap, orgRoutes],
  );

  const userColumns: DataTableColumn<UsageUserTotals>[] = useMemo(() => {
    const cols: DataTableColumn<UsageUserTotals>[] = [
      {
        header: 'User',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5',
        cell: (row) => {
          const displayName = row.name ?? row.login ?? 'Unknown';
          return row.userId ? (
            <Link
              to={ROUTES.userProfile(row.userId)}
              className="flex items-center gap-2 hover:underline"
            >
              <UserAvatar avatarUrl={row.avatarUrl ?? undefined} userName={displayName} size="xs" />
              <span className="text-sm">{displayName}</span>
            </Link>
          ) : (
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <UserAvatar userName={displayName} size="xs" />
              {displayName}
            </span>
          );
        },
      },
    ];
    if (showUserOrgColumn) {
      cols.push({
        header: 'Organization',
        headerClassName: 'whitespace-nowrap',
        cellClassName: 'py-2.5 text-xs text-muted-foreground',
        cell: (row) => orgLabel(row.organizationId, row.organizationName, row.organizationSlug),
      });
    }
    cols.push(
      {
        header: 'Running agents',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.runningAgents,
      },
      {
        header: 'Active streams',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.activeStreams,
      },
      {
        header: 'Sockets',
        headerClassName: 'whitespace-nowrap text-right',
        cellClassName: 'whitespace-nowrap py-2.5 text-right tabular-nums',
        cell: (row) => row.connectedSockets,
      },
    );
    return cols;
  }, [showUserOrgColumn]);

  const orgSelect = (
    <FilterSearchSelect
      label="Organization"
      value={rawOrg ?? ALL_ORGS}
      onValueChange={(v) => void setRawOrg(v === ALL_ORGS ? null : v)}
      options={orgOptions}
      searchPlaceholder="Search orgs…"
      className="w-52"
    />
  );

  return (
    <div className="space-y-4" data-testid="admin-usage-panel">
      <CardGrid cols={3}>
        <StatCard
          label="Active Streams"
          value={tiles?.activeStreams ?? '—'}
          icon={<Radio className="h-5 w-5" />}
          description="Open Axon streams"
        />
        <StatCard
          label="Running Agents"
          value={tiles?.runningAgents ?? '—'}
          icon={<Bot className="h-5 w-5" />}
          description="Agents in starting/running/needs_input"
        />
        <StatCard
          label="Connected Clients"
          value={tiles?.connectedClients ?? '—'}
          icon={<Plug className="h-5 w-5" />}
          description="Live WebSocket connections"
        />
      </CardGrid>

      <SectionCard
        title="Per-Org Usage"
        icon={<Building2 />}
        description="Running agents, streams, and sockets broken down by organization. Agentless streams are typically automation runs and have no owning org."
        action={orgSelect}
        headerClassName="pb-3"
        contentClassName="px-0 pb-0"
      >
        {!snapshot ? (
          <EmptyState
            icon={<Building2 className="h-5 w-5 text-muted-foreground" />}
            title="Waiting for first usage snapshot…"
            className="py-10"
          />
        ) : snapshot.perOrg.length === 0 ? (
          <EmptyState
            icon={<Building2 className="h-5 w-5 text-muted-foreground" />}
            title="No usage to report"
            className="py-10"
          />
        ) : (
          <DataTable<UsageOrgTotals>
            data={snapshot.perOrg}
            columns={orgColumns}
            rowKey={(row) => row.organizationId || NO_ORG}
          />
        )}
      </SectionCard>

      <SectionCard
        title="Active Streams"
        icon={<Radio />}
        description="Live Axon streams updated every few seconds. Stream IDs are truncated."
        headerClassName="pb-3"
        contentClassName="px-0 pb-0"
      >
        {!snapshot ? (
          <EmptyState
            icon={<Radio className="h-5 w-5 text-muted-foreground" />}
            title="Waiting for first usage snapshot…"
            className="py-10"
          />
        ) : filteredStreams.length === 0 ? (
          <EmptyState
            icon={<Radio className="h-5 w-5 text-muted-foreground" />}
            title="No active streams"
            className="py-10"
          />
        ) : (
          <DataTable<UsageStream>
            data={filteredStreams}
            columns={streamColumns}
            rowKey={(stream) => stream.streamId}
          />
        )}
      </SectionCard>

      <SectionCard
        title="Per-User Usage"
        icon={<Users />}
        description="Running agents, streams, and sockets by owner. Users appear once per organization they have activity in."
        headerClassName="pb-3"
        contentClassName="px-0 pb-0"
      >
        {!snapshot ? (
          <EmptyState
            icon={<Users className="h-5 w-5 text-muted-foreground" />}
            title="Waiting for first usage snapshot…"
            className="py-10"
          />
        ) : filteredPerUser.length === 0 ? (
          <EmptyState
            icon={<Users className="h-5 w-5 text-muted-foreground" />}
            title="No usage to report"
            className="py-10"
          />
        ) : (
          <DataTable<UsageUserTotals>
            data={filteredPerUser}
            columns={userColumns}
            rowKey={(row) => `${row.organizationId || NO_ORG}:${row.userId ?? 'unknown'}`}
          />
        )}
      </SectionCard>
    </div>
  );
}
